import { useEffect, useRef } from "react";
import { Box } from "@mui/material";
import { DotLegalDatePicker, DotLegalInformationTooltip, DotLegalSelect, DotLegalTextField } from "@dotlegal/dotlegal-ui-components";
import { useTranslation } from "@localization/useTranslation";
import useCreateTaskPane from "./CreateTaskPane.hooks";
import PaneLayout, { PaneTitle } from "../../main/panes/paneLayout/PaneLayout";
import { usePane } from "../../main/panes/paneLayout/usePane";
import { definePane } from "../../main/panes/definePane";
import { usePanes } from "../../main/panes/usePanes";
import { AddTaskRelationData } from "./CreateTaskPane.types";
import TaskPane from "../taskPane/TaskPane";

export interface CreateTaskPaneProps {
    relationInformation?: AddTaskRelationData;
    prefixName?: string;
    /** Runs once the task exists, for a caller with something to refresh that the pane store's refetch registry cannot reach. */
    onCreated?: (taskId: string) => void;
}

/**
 * The create form as a rail occupant. The form itself is unchanged: useCreateTaskPane knows nothing
 * about the surface it is shown on, so only the dialog around it is replaced. Once the task exists it goes
 * to the stage and this pane retires, since opening a stage pane leaves the rail alone.
 */
function CreateTaskPane(props: CreateTaskPaneProps) {
    const { translateString, getDateTimePickerLanguage } = useTranslation();
    const { close, setHasUnsavedChanges } = usePane();
    const { open } = usePanes();

    const onCreated = props.onCreated;
    const { saveModel, validation, onModelChange, onSave, responsibleData, responsibleIsLoading, convertDate, isSaving } = useCreateTaskPane({
        relationInformation: props.relationInformation,
        prefixName: props.prefixName,
        onTaskCreated: (taskId) => {
            // Nothing typed is at risk any more, and the confirmation would otherwise stand between the
            // user and the task they just made.
            setHasUnsavedChanges(false);
            open(TaskPane, { taskId });
            close();
            onCreated?.(taskId);
        },
    });

    const nameRef = useRef<HTMLTextAreaElement>();
    useEffect(() => {
        setTimeout(() => {
            nameRef.current?.focus();
        }, 0);
    }, []);

    return (
        <PaneLayout
            reserveFieldTooltipSpace
            header={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PaneTitle>{translateString("createTask")}</PaneTitle>
                    <DotLegalInformationTooltip text={translateString("createAdHocTaskInfoText")} />
                </Box>
            }
            okButtonText={translateString("create")}
            onOkClick={() => onSave()}
            inProgress={isSaving}
        >
            <DotLegalTextField
                inputRef={nameRef}
                label={translateString("name")}
                value={saveModel?.name ?? ""}
                debounce={false}
                errorText={validation?.getErrorsForDisplay("name")}
                onChange={(name) => {
                    setHasUnsavedChanges(true);
                    onModelChange((model) => (model.name = name));
                }}
                toolTipText={translateString("taskNameTooltip")}
            />
            <DotLegalSelect
                options={responsibleData}
                isLoading={responsibleIsLoading}
                selectedItem={saveModel.responsible}
                placeholder={translateString("noResponsible")}
                label={translateString("responsible")}
                onChange={(responsible) => {
                    setHasUnsavedChanges(true);
                    onModelChange((model) => (model.responsible = responsible));
                }}
                noOptionsLabel={translateString("noOptions")}
                errorText={validation?.getErrorsForDisplay("responsible")}
                toolTipText={translateString("taskResponsibleTooltip")}
            />
            <DotLegalDatePicker
                label={translateString("deadline")}
                value={saveModel.deadline}
                onChange={(date: Date | null) => {
                    setHasUnsavedChanges(true);
                    onModelChange((model) => (model.deadline = convertDate(date!)));
                }}
                language={getDateTimePickerLanguage()}
                error={validation.getErrorsForDisplay("deadline") !== undefined}
                helperMessage={validation?.getErrorsForDisplay("deadline")}
                disableKeyboardInput
                toolTipText={translateString("taskDeadlineTooltip")}
                maxDate={new Date(new Date().getFullYear() + 1, 11, 31)}
            />
        </PaneLayout>
    );
}

export default definePane({ slot: "right", component: CreateTaskPane });
